import { roomGroupIds } from '../../utils/roomGroupUtils';

// Pure data-view helpers shared by the floorplan renderer and gestures.
//
// These were inline closures in UVFloorPlanCanvas that read `selectedRoom` /
// `structureData` / `structureWalls` from component scope. Phase 2a moves the
// LOGIC here as pure functions (dependencies passed explicitly) so the renderer
// can be a pure function of its contracts. Call sites keep their original names
// via thin one-line wrappers in the coordinator and the renderer, so the bodies
// at every call site stay byte-identical.

// The canonical structure-level doors / windows / openings that `rooms`
// reference (a slim copy's `structureOpeningId`, or a legacy fat copy's own
// `id`), taken from `structureOpenings`.
//
// Never the room copies themselves: post-PR #634 they are slim (no center /
// dimensions), so pulling from them would leave the opening arrays empty of
// geometry — `drawWalls` would silently skip wall-gap carving and walls
// render solid through every door / window / opening. And never the
// structure arrays unfiltered: they hold every opening in the building, on
// every storey, so a plan of some rooms would draw doors on other rooms'
// walls — and, stacked in x/z, the floor above's.
export function openingsReferencedByRooms(rooms, structureOpenings) {
  const referenced = (field) =>
    new Set(
      (rooms || [])
        .flatMap((r) => r?.[field] || [])
        .map((el) => el?.structureOpeningId || el?.id)
        .filter(Boolean)
    );
  const pick = (field) => {
    const ids = referenced(field);
    return (structureOpenings?.[field] || []).filter((o) => ids.has(o.id));
  };
  return {
    doors: pick('doors'),
    windows: pick('windows'),
    openings: pick('openings'),
  };
}

// Filter a structure-shaped data object down to a single selected room's
// elements (verbatim from UVFloorPlanCanvas.getDataToRender; `selectedRoom`
// is now an explicit parameter).
export function filterDataToSelectedRoom(dataParam, selectedRoom) {
  if (!selectedRoom || !dataParam) {
    return dataParam;
  }

  // The scope is the room GROUP, not the one room: a satellite (a closet /
  // pantry / nook the pipeline carved off this room — see
  // `utils/roomGroupUtils`) belongs to it, so scoping the canvas to a
  // bedroom must not clip its own closet off the drawing. `selectedRoom`
  // stays first and keeps its own (possibly live-edited) copy; the rest are
  // read from `dataParam.rooms`. A room with no satellites yields a group of
  // one and the arrays below are exactly what they were.
  const groupIds = roomGroupIds(selectedRoom, dataParam.rooms);
  const group = [
    selectedRoom,
    ...(dataParam.rooms || []).filter(
      (r) => r?.id && r.id !== selectedRoom.id && groupIds.has(r.id)
    ),
  ];
  // Identity-preserving for the single-member case: a room with no
  // satellites hands back its OWN arrays, byte-for-byte what this returned
  // before grouping existed, so nothing downstream that memoizes on array
  // identity re-renders for a feature it doesn't have.
  const groupFlat = (key) =>
    group.length === 1
      ? group[0]?.[key] || []
      : group.flatMap((r) => r?.[key] || []);

  // If we have a selected room, filter data to only show elements related to that room
  if (
    selectedRoom.walls ||
    selectedRoom.doors ||
    selectedRoom.windows ||
    selectedRoom.floors ||
    selectedRoom.objects ||
    selectedRoom.openings
  ) {
    // doors / windows / openings come from `dataParam` (canonical
    // structure-level arrays), NOT from the room copies — see
    // `openingsReferencedByRooms`. The room's own slim copies are still
    // present in `selectedRoom`, included in `rooms: [selectedRoom]` below so
    // per-room iteration (which hydrates against the structure lookup) still
    // works.
    const filtered = {
      walls: groupFlat('walls'),
      ...openingsReferencedByRooms(group, dataParam),
      floors: groupFlat('floors'),
      objects: groupFlat('objects'),
      rooms: group,
    };

    return filtered;
  }

  return dataParam;
}

// Whether an item is a wall (verbatim from UVFloorPlanCanvas.isWall;
// `structureData` / `structureWalls` are now explicit parameters).
export function itemIsWall(item, structureData, structureWalls) {
  if (!item) return false;
  const itemId = item.id;
  if (!itemId) return false;

  // If item is marked as a structure wall from click detection, it's a wall
  if (item._isStructureWall) {
    return true;
  }

  // Check room walls
  if (structureData?.rooms) {
    for (const room of structureData.rooms) {
      if (room.walls?.some((w) => w && w.id === itemId)) {
        return true;
      }
    }
  }

  // Check structure walls
  if (structureWalls?.some((sw) => sw && sw.id === itemId)) {
    return true;
  }

  return false;
}

// Flatten room elements of a given type, tagging each with its room id
// (verbatim from UVFloorPlanCanvas.getElementsWithRoomContext — already pure).
export function getElementsWithRoomContext(rooms, elementType) {
  if (!rooms || !Array.isArray(rooms)) return [];

  const elements = [];
  rooms.forEach((room) => {
    const roomElements = room[elementType] || [];
    roomElements.forEach((element) => {
      // Skip null/undefined elements
      if (!element) {
        return;
      }
      // Add room ID to element for context
      elements.push({
        ...element,
        _roomId: room.id,
      });
    });
  });

  return elements;
}

/**
 * The structure-level walls that back a set of room walls: a room wall
 * points at its structure wall through `alignment.structureWallId`, and a
 * scanned wall shares its id with the structure copy. Both the renderer
 * (what to draw in single-room view) and the click hit-test (what can be
 * selected there) go through this, so a wall that isn't drawn can't be
 * clicked — a blank spot used to select an invisible wall from another room.
 */
export function structureWallsForRoomWalls(structureWalls, roomWalls) {
  if (!Array.isArray(structureWalls) || structureWalls.length === 0) return [];
  const structureIds = new Set(structureWalls.map((sw) => sw.id));
  const associated = new Set();
  for (const wall of roomWalls || []) {
    if (wall?.alignment?.structureWallId)
      associated.add(wall.alignment.structureWallId);
    if (structureIds.has(wall?.id)) associated.add(wall.id);
  }
  return structureWalls.filter((sw) => associated.has(sw.id));
}
